/**
 * "Sales rep and CSR" on a customer's Settings tab (v1 completion C5,
 * mock-up state 7): who holds the customer on each axis, as its portfolios
 * say. Managers and admins (`account_allocation:manage`) change them; the
 * view says whether the caller may (`can_edit`) and where a change goes
 * (`allocate_url`, account allocation's action).
 *
 * A customer linked to Tharstern has the change copied to Tharstern first,
 * so it offers people only: it can't go to House Account from CIS, and an
 * axis on House Account is left as it is unless someone is chosen. A
 * customer created in CIS may go to House Account.
 *
 * Hidden for a caller who may not see the customer (the read answers 403 or
 * 404). Loads and saves on its own, outside credit control's Save bar.
 */
import { useCallback, useEffect, useId, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { ApiError } from "@shared-core/data/api/errors";
import type {
  CustomerOwnerOption,
  CustomerOwners,
} from "@shared-core/data/api-types";
import { extractFieldErrors } from "@shared-core/data/validation";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import {
  HOUSE_ACCOUNT,
  bindNewCustomerErrors,
  type NewCustomerField,
} from "@shared-core/domain/new-customer";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Button } from "../../primitives/button";
import { Skeleton } from "../../primitives/skeleton";

type Load =
  | { kind: "loading" }
  | { kind: "hidden" }
  | { kind: "failed"; message: string }
  | { kind: "loaded"; owners: CustomerOwners };

const SELECT_CLASS =
  "border-input bg-input-background flex h-9 w-full rounded-md border px-3 py-1 text-sm outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:border-destructive disabled:opacity-50";

export function CustomerOwnersSection({
  customerKey,
}: {
  /** The customer page's key: its Tharstern code, or its `CUS-` code. */
  customerKey: string;
}) {
  const adapter = useDataAdapter();
  const idPrefix = useId();
  const [load, setLoad] = useState<Load>({ kind: "loading" });
  const [salesRep, setSalesRep] = useState(HOUSE_ACCOUNT);
  const [csr, setCsr] = useState(HOUSE_ACCOUNT);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<
    Partial<Record<NewCustomerField, string>>
  >({});
  const [failure, setFailure] = useState<string | null>(null);

  const show = useCallback((owners: CustomerOwners) => {
    setLoad({ kind: "loaded", owners });
    setSalesRep(owners.sales_rep?.user_id ?? HOUSE_ACCOUNT);
    setCsr(owners.csr?.user_id ?? HOUSE_ACCOUNT);
  }, []);

  const reload = useCallback(() => {
    setLoad({ kind: "loading" });
    adapter
      .getCustomerOwners(customerKey)
      .then(show)
      .catch((err: unknown) => {
        if (
          err instanceof ApiError &&
          (err.status === 403 || err.status === 404)
        ) {
          setLoad({ kind: "hidden" });
          return;
        }
        setLoad({
          kind: "failed",
          message: mutationErrorMessage(err, "the server did not answer"),
        });
      });
  }, [adapter, customerKey, show]);

  useEffect(() => {
    reload();
  }, [reload]);

  if (load.kind === "hidden") return null;

  const save = async (owners: CustomerOwners) => {
    // The form shows only when the command is presented and enabled, and then its URL is there.
    if (!owners.allocate_url) return;
    setSaving(true);
    setErrors({});
    setFailure(null);
    try {
      await adapter.allocateCustomer(owners.allocate_url, {
        sales_rep_user_id: salesRep || null,
        csr_user_id: csr || null,
      });
      show(await adapter.getCustomerOwners(customerKey));
      toast.success("Sales rep and CSR saved");
    } catch (err) {
      const fieldErrors = extractFieldErrors(err);
      if (fieldErrors.length > 0) {
        const bound = bindNewCustomerErrors(fieldErrors);
        setErrors(bound.byField);
        if (bound.unbound.length > 0) setFailure(bound.unbound.join(" "));
      } else {
        setFailure(
          mutationErrorMessage(err, "Not saved: the server did not answer."),
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      aria-labelledby={`${idPrefix}-heading`}
      className="space-y-4 rounded-lg border border-border p-4 text-sm"
    >
      <h2 id={`${idPrefix}-heading`} className="text-base font-medium">
        Sales rep and CSR
      </h2>
      {load.kind === "loading" && (
        <div role="status" aria-label="Loading sales rep and CSR">
          <Skeleton className="h-4 w-1/2" />
        </div>
      )}
      {load.kind === "failed" && (
        <Alert variant="destructive" role="alert">
          <AlertDescription>
            Could not load the sales rep and CSR: {load.message}{" "}
            <button type="button" className="underline" onClick={reload}>
              Try again
            </button>
          </AlertDescription>
        </Alert>
      )}
      {load.kind === "loaded" &&
        (load.owners.can_edit ? (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              void save(load.owners);
            }}
          >
            {failure && (
              <p
                role="alert"
                className="rounded-md border border-red-300 px-3 py-2 text-red-700 dark:border-red-800 dark:text-red-400"
              >
                {failure}
              </p>
            )}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <AxisSelect
                id={`${idPrefix}-sales-rep`}
                label="Sales rep"
                value={salesRep}
                current={load.owners.sales_rep}
                options={load.owners.sales_rep_options ?? []}
                isLinked={load.owners.tharstern_customer_code !== null}
                onChange={setSalesRep}
                disabled={saving}
                error={errors.salesRep}
              />
              <AxisSelect
                id={`${idPrefix}-csr`}
                label="CSR"
                value={csr}
                current={load.owners.csr}
                options={load.owners.csr_options ?? []}
                isLinked={load.owners.tharstern_customer_code !== null}
                onChange={setCsr}
                disabled={saving}
                error={errors.csr}
              />
            </div>
            <LinkHints owners={load.owners} />
            <div className="flex justify-end">
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="size-3.5 animate-spin" />}
                Save
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-2">
            <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div>
                <dt className="text-xs text-muted-foreground">Sales rep</dt>
                <dd>{load.owners.sales_rep?.name ?? "Not set yet"}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">CSR</dt>
                <dd>{load.owners.csr?.name ?? "Not set yet"}</dd>
              </div>
            </dl>
            <p className="text-xs text-muted-foreground">
              Managers and admins change these.
            </p>
          </div>
        ))}
    </section>
  );
}

function AxisSelect({
  id,
  label,
  value,
  current,
  options,
  isLinked,
  onChange,
  disabled,
  error,
}: {
  id: string;
  label: string;
  value: string;
  current: CustomerOwners["sales_rep"];
  options: readonly CustomerOwnerOption[];
  isLinked: boolean;
  onChange: (value: string) => void;
  disabled: boolean;
  error: string | undefined;
}) {
  // A linked customer on House Account (or not yet held) shows that as its
  // current value, but it can't be chosen: picking a person is the only change.
  const houseAccountLabel =
    isLinked && current?.user_id == null
      ? current
        ? "House Account"
        : "Not set yet"
      : "House Account";
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        className={SELECT_CLASS}
      >
        {(!isLinked || value === HOUSE_ACCOUNT) && (
          <option value={HOUSE_ACCOUNT} disabled={isLinked}>
            {houseAccountLabel}
          </option>
        )}
        {options.map((option) => (
          <option key={option.user_id} value={option.user_id}>
            {option.name}
          </option>
        ))}
      </select>
      {error && (
        <p
          id={`${id}-error`}
          className="text-sm text-red-600 dark:text-red-400"
        >
          {error}
        </p>
      )}
    </div>
  );
}

function LinkHints({ owners }: { owners: CustomerOwners }) {
  if (owners.tharstern_customer_code === null) return null;
  return (
    <div className="space-y-1 text-xs text-muted-foreground">
      <p>Linked to Tharstern: changes are copied to Tharstern too.</p>
      {owners.mastered_in === "tharstern" && (
        <p>Changes made in Tharstern will still reach this customer.</p>
      )}
    </div>
  );
}
